import { Box, Typography } from '@mui/material';
import { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  FormikCheckbox,
  FormikNumericField,
  FormikSelect,
  FormikTextField,
  HelpTooltip,
} from 'components';
import { P2PProviderType } from 'enums';
import { useActiveDynamicAdapterConfig } from 'features/dynamic-adapter-configs';
import { TranslationNamespace } from 'i18n';

type Props = {
  path: string;
  providerType?: P2PProviderType | string;
};

const KNOWN_ENUM_TYPES = new Set<string>(Object.values(P2PProviderType));

export const P2PProviderCredentials: React.FC<Props> = ({
  path,
  providerType,
}) => {
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'pages.p2p_providers',
  });

  const isDynamicType = !!providerType && !KNOWN_ENUM_TYPES.has(providerType);
  const { config: dynamicConfig } = useActiveDynamicAdapterConfig(
    isDynamicType ? providerType : undefined,
  );

  const dynamicCredentialFields = useMemo(() => {
    if (!isDynamicType) {
      return [] as string[];
    }
    const fromPayin = dynamicConfig?.payinConfig?.requiredCredentials || [];
    const fromPayout = dynamicConfig?.payoutConfig?.requiredCredentials || [];
    return Array.from(new Set([...fromPayin, ...fromPayout]));
  }, [dynamicConfig, isDynamicType]);

  return (
    <Fragment>
      <Typography>{t('fields.credentials')}</Typography>
      {isDynamicType && (
        <Fragment>
          {dynamicCredentialFields.length === 0 && (
            <Typography variant="caption" color="text.secondary">
              {t('fields.dynamic_credentials_hint')}
            </Typography>
          )}
          {dynamicCredentialFields.map((field) => (
            <FormikTextField
              key={field}
              label={field}
              name={`${path}.${field}`}
            />
          ))}
        </Fragment>
      )}
      {providerType === P2PProviderType.InternalLike && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikTextField
            label={t('fields.signature_key')}
            name={`${path}.signatureKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Trust2Pay && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_secret')}
            name={`${path}.apiSecret`}
          />
          <FormikTextField
            label={t('fields.password')}
            name={`${path}.password`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.X2X && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_secret')}
            name={`${path}.apiSecret`}
          />
          <FormikTextField
            label={t('fields.password')}
            name={`${path}.password`}
          />
        </Fragment>
      )}
      {(providerType === P2PProviderType.Payscrow ||
        providerType === P2PProviderType.PayscrowMobile) && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Alaris && (
        <Fragment>
          <FormikTextField
            label={t('fields.project_id')}
            name={`${path}.projectId`}
          />
          <FormikTextField
            label={t('fields.api_private')}
            name={`${path}.privateKey`}
            multiline
            rows={4}
          />
          <FormikTextField
            label={t('fields.api_public')}
            name={`${path}.publicKey`}
            multiline
            rows={4}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Namba && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_token')}
            name={`${path}.apiToken`}
          />
        </Fragment>
      )}
      {(providerType === P2PProviderType.NambaOne ||
        providerType === P2PProviderType.Npay) && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_secret')}
            name={`${path}.secret`}
          />
          <FormikTextField
            label={t('fields.merchant_id')}
            name={`${path}.merchantAccountGuid`}
          />
          <Box display="flex" alignItems="center">
            <FormikTextField
              label={t('fields.create_api_version')}
              name={`${path}.createApiVersion`}
            />
            <HelpTooltip title={t('fields.create_api_version_description')} />
          </Box>
          <FormikTextField
            label={t('fields.webhook_secret')}
            name={`${path}.webhookSecret`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.NambaHyperion && (
        <Fragment>
          <FormikTextField label={t('fields.login')} name={`${path}.login`} />
          <FormikTextField
            label={t('fields.password')}
            name={`${path}.password`}
          />
          <FormikTextField label={t('fields.dealer')} name={`${path}.dealer`} />
          <FormikTextField
            label={t('fields.default_bearer')}
            name={`${path}.defaultBearer`}
          />
          <FormikCheckbox
            label={t('fields.exclude_commission')}
            name={`${path}.excludeCommission`}
          />
          <FormikCheckbox
            label={t('fields.keep_plus')}
            name={`${path}.keepPlus`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Noros && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Duna && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikTextField
            label={t('fields.webhook_secret')}
            name={`${path}.webhookSecret`}
          />
          <FormikTextField
            label={t('fields.ca_cert')}
            name={`${path}.caCert`}
            multiline
            rows={4}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Paycraft && (
        <Fragment>
          <FormikTextField
            label={t('fields.client_code_name')}
            name={`${path}.clientCodeName`}
          />
          <FormikTextField
            label={t('fields.api_private')}
            name={`${path}.privateKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.PaycraftElqr && (
        <Fragment>
          <FormikTextField
            label={t('fields.point_id')}
            name={`${path}.pointId`}
          />
          <FormikTextField
            label={t('fields.api_private')}
            name={`${path}.privateKey`}
            multiline
            rows={4}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.PaycraftV1_1 && (
        <Fragment>
          <FormikTextField
            label={t('fields.client_id')}
            name={`${path}.clientId`}
          />
          <FormikTextField
            label={t('fields.api_token')}
            name={`${path}.apiToken`}
          />
          <FormikTextField
            label={t('fields.webhook_secret')}
            name={`${path}.webhookSecret`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Exwave && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_token')}
            name={`${path}.apiToken`}
          />
          <FormikTextField
            label={t('fields.host2host')}
            name={`${path}.host2host`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Reactivepay && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_token')}
            name={`${path}.apiToken`}
          />
          <FormikTextField
            label={t('fields.api_private')}
            name={`${path}.privateKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Realpay && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikTextField
            label={t('fields.shop_id')}
            name={`${path}.shopId`}
          />
          <FormikCheckbox label={t('fields.is_ftd')} name={`${path}.isFtd`} />
        </Fragment>
      )}
      {providerType === P2PProviderType.Mapsign && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikTextField
            label={t('fields.basic_auth')}
            name={`${path}.basicAuth`}
          />
          <FormikTextField
            label={t('fields.bearer_token')}
            name={`${path}.bearerToken`}
          />
        </Fragment>
      )}
      {(providerType === P2PProviderType.Titanpay ||
        providerType === P2PProviderType.TitanpayAlfa) && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_token')}
            name={`${path}.apiToken`}
          />
          <FormikTextField
            label={t('fields.api_private')}
            name={`${path}.privateKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Fintrix && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_token')}
            name={`${path}.apiToken`}
          />
          <FormikTextField label={t('fields.salt')} name={`${path}.salt`} />
        </Fragment>
      )}
      {providerType === P2PProviderType.Payfinity && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_public')}
            name={`${path}.publicKey`}
          />
          <FormikTextField
            label={t('fields.api_private')}
            name={`${path}.privateKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.XPayments && (
        <Fragment>
          <FormikTextField
            label={t('fields.merchant_id')}
            name={`${path}.merchantId`}
          />
          <FormikTextField
            label={t('fields.contract_id')}
            name={`${path}.contractId`}
          />
          <FormikTextField label={t('fields.login')} name={`${path}.login`} />
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.MoneyMania && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikTextField
            label={t('fields.api_public')}
            name={`${path}.webhookPublicKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.MoneyHub && (
        <Fragment>
          <FormikTextField
            label={t('fields.access_token_payin')}
            name={`${path}.accessTokenPayin`}
          />
          <FormikTextField
            label={t('fields.access_token_payout')}
            name={`${path}.accessTokenPayout`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.IDMPay && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikTextField
            label={t('fields.customer_uuid')}
            name={`${path}.customerUUID`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.P2PTrade && (
        <Fragment>
          <FormikTextField
            label={t('fields.client_id')}
            name={`${path}.clientId`}
          />
          <FormikTextField
            label={t('fields.api_secret')}
            name={`${path}.apiSecret`}
          />
        </Fragment>
      )}
      {(providerType === P2PProviderType.Bridgepay ||
        providerType === P2PProviderType.Beikra) && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikTextField
            label={t('fields.api_secret')}
            name={`${path}.apiSecret`}
          />
          <FormikCheckbox
            label={t('fields.force_invoice_url')}
            name={`${path}.forceInvoiceUrl`}
          />
          <FormikNumericField
            label={t('fields.randomization_max_amount_delta')}
            name={`${path}.maxAmount`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.OnlyOnePays && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikTextField
            label={t('fields.default_bank_id')}
            name={`${path}.defaultBankId`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Mercurio && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Airpay && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_token')}
            name={`${path}.apiToken`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Flackopay && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_token')}
            name={`${path}.apiToken`}
          />
          <FormikTextField
            label={t('fields.api_token_payout_sbp')}
            name={`${path}.apiTokenPayoutSbp`}
          />
          <FormikTextField
            label={t('fields.api_token_payout_card')}
            name={`${path}.apiTokenPayoutCard`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Garex && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_token')}
            name={`${path}.token`}
          />
          <FormikTextField
            label={t('fields.merchant_id')}
            name={`${path}.merchantId`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.GiPay && (
        <Fragment>
          <FormikTextField
            label={t('fields.merchant_id')}
            name={`${path}.merchantId`}
          />
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikTextField
            label={t('fields.secret_key')}
            name={`${path}.secretKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.NoName && (
        <Fragment>
          <FormikTextField
            label={t('fields.auth_token')}
            name={`${path}.authToken`}
          />
          <FormikTextField
            label={t('fields.signature_key')}
            name={`${path}.privateKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Pentax && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_token')}
            name={`${path}.apiToken`}
          />
          <FormikTextField
            label={t('fields.api_private')}
            name={`${path}.privateKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.BBRPay && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Kingsman && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikTextField
            label={t('fields.api_secret')}
            name={`${path}.apiSecret`}
          />
          <FormikTextField
            label={t('fields.account_id')}
            name={`${path}.accountId`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Panda && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikTextField
            label={t('fields.api_secret')}
            name={`${path}.apiSecret`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Pay2Play && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_token')}
            name={`${path}.apiToken`}
          />
          <FormikTextField
            label={t('fields.webhook_public_key')}
            name={`${path}.webhookPublicKey`}
            multiline
            rows={4}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.ManyPay && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikTextField
            label={t('fields.api_secret')}
            name={`${path}.apiSecret`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.PayCrown && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikTextField
            label={t('fields.api_secret')}
            name={`${path}.apiSecret`}
          />
          <FormikTextField
            label={t('fields.merchant_id')}
            name={`${path}.merchantId`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.PayPlace && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.BeePay && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Gotham && (
        <Fragment>
          <FormikTextField label={t('fields.login')} name={`${path}.login`} />
          <FormikTextField
            label={t('fields.api_token')}
            name={`${path}.apiToken`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Pulsar && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_secret')}
            name={`${path}.apiSecret`}
          />
          <FormikTextField
            label={t('fields.api_token')}
            name={`${path}.apiToken`}
          />
          <FormikTextField
            label={t('fields.merchant_id')}
            name={`${path}.merchantId`}
          />
          <FormikSelect
            label={t('fields.webhook_amount_field')}
            name={`${path}.webhookAmountField`}
            options={[
              { value: 'amount', label: 'amount' },
              { value: 'init_amount', label: 'init_amount' },
            ]}
          />
        </Fragment>
      )}
      {(providerType === P2PProviderType.BNPay ||
        providerType === P2PProviderType.SwipePay ||
        providerType === P2PProviderType.SwipePayV1) && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikTextField
            label={t('fields.api_secret')}
            name={`${path}.apiSecret`}
          />
          <FormikTextField
            label={t('fields.shop_id')}
            name={`${path}.shopId`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Montra && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_token')}
            name={`${path}.apiToken`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.PayMatrix && (
        <Fragment>
          <FormikSelect
            label={t('fields.flow_mode')}
            name={`${path}.flowMode`}
            options={[
              { value: 'requisite', label: 'Requisite' },
              { value: 'payment_form', label: 'Payment Form' },
            ]}
          />
          <FormikTextField label={t('fields.login')} name={`${path}.login`} />
          <FormikTextField
            label={t('fields.otp_secret')}
            name={`${path}.otpSecret`}
          />
          <FormikTextField
            label={t('fields.sso_url')}
            name={`${path}.ssoUrl`}
          />
          <FormikTextField
            label={t('fields.payment_form_merchant')}
            name={`${path}.paymentFormMerchant`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.TrustPay && (
        <Fragment>
          <FormikTextField label={t('fields.login')} name={`${path}.login`} />
          <FormikTextField
            label={t('fields.merchant_id')}
            name={`${path}.merchantId`}
          />
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.TrustCloud && (
        <Fragment>
          <FormikTextField
            label={t('fields.casino_hash')}
            name={`${path}.casinoHash`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.EHotPay && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_token')}
            name={`${path}.apiToken`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.EpayBrl && (
        <Fragment>
          <FormikTextField
            label={t('fields.merchant_id')}
            name={`${path}.merchantId`}
          />
          <FormikTextField label={t('fields.app_id')} name={`${path}.appId`} />
          <FormikTextField
            label={t('fields.secret_key')}
            name={`${path}.secretKey`}
          />
        </Fragment>
      )}
      {(providerType === P2PProviderType.Profitable ||
        providerType === P2PProviderType.ProfitableLight) && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_token')}
            name={`${path}.apiToken`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Cactus && (
        <Fragment>
          <FormikTextField
            label={t('fields.site_id')}
            name={`${path}.siteId`}
          />
          <FormikTextField
            label={t('fields.site_login')}
            name={`${path}.siteLogin`}
          />
          <FormikTextField
            label={t('fields.merchant_id')}
            name={`${path}.merchantId`}
          />
          <FormikTextField label={t('fields.salt')} name={`${path}.salt`} />
        </Fragment>
      )}
      {providerType === P2PProviderType.Arhitec && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_token')}
            name={`${path}.apiToken`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Europays && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_token')}
            name={`${path}.apiToken`}
          />
          <FormikTextField
            label={t('fields.secret_key')}
            name={`${path}.secretKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Astrum && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikTextField
            label={t('fields.fernet_key')}
            name={`${path}.fernetKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Coince && (
        <Fragment>
          <FormikTextField
            label={t('fields.access_token')}
            name={`${path}.accessToken`}
          />
          <FormikTextField
            label={t('fields.secret_key')}
            name={`${path}.secretKey`}
          />
          <FormikTextField
            label={t('fields.merchant_id')}
            name={`${path}.merchantId`}
          />
          <FormikTextField
            label={t('fields.default_payout_gateway')}
            name={`${path}.defaultPayoutGateway`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.BitWire && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikTextField label={t('fields.login')} name={`${path}.login`} />
          <FormikTextField
            label={t('fields.password')}
            name={`${path}.password`}
          />
          <FormikTextField
            label={t('fields.account_id')}
            name={`${path}.accountId`}
          />
          <FormikCheckbox
            label={t('fields.send_customer_data')}
            name={`${path}.sendCustomerData`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.BlackStar && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikTextField
            label={t('fields.webhook_secret')}
            name={`${path}.webhookSecret`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Stbl && (
        <Fragment>
          <FormikTextField
            label={t('fields.username')}
            name={`${path}.username`}
          />
          <FormikTextField
            label={t('fields.password')}
            name={`${path}.password`}
          />
          <FormikTextField
            label={t('fields.secret_key')}
            name={`${path}.secretKey`}
          />
        </Fragment>
      )}
      {(providerType === P2PProviderType.Bova ||
        providerType === P2PProviderType.BovaMini) && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikTextField
            label={t('fields.user_id')}
            name={`${path}.userId`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Brusnikapay && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_token')}
            name={`${path}.apiToken`}
          />
          <FormikTextField
            label={t('fields.webhook_public_key')}
            name={`${path}.webhookPublicKey`}
            multiline
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.PumP2P && (
        <Fragment>
          <FormikTextField
            label={t('fields.auth_token')}
            name={`${path}.authToken`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.RapidPays && (
        <Fragment>
          <FormikTextField
            label={t('fields.secret_key')}
            name={`${path}.secretKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.FreeFlow && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_token')}
            name={`${path}.apiToken`}
          />
          <FormikTextField
            label={t('fields.shop_id')}
            name={`${path}.shopId`}
          />
          <FormikTextField
            label={t('fields.secret_key')}
            name={`${path}.secret1`}
          />
          <FormikTextField
            label={t('fields.webhook_secret')}
            name={`${path}.secret2`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Forta && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_token')}
            name={`${path}.apiToken`}
          />
          <FormikTextField
            label={t('fields.api_private')}
            name={`${path}.privateKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Incas && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_token')}
            name={`${path}.apiToken`}
          />
          <FormikSelect
            label={t('fields.webhook_amount_field')}
            name={`${path}.webhookAmountField`}
            options={[
              { value: 'amount', label: 'amount' },
              { value: 'amountSource', label: 'amountSource' },
            ]}
          />
          <FormikTextField
            label={t('fields.default_bank')}
            name={`${path}.defaultPayinBank`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.IronPay && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.InfinityPay && (
        <Fragment>
          <FormikTextField
            label={t('fields.merchant_token')}
            name={`${path}.merchantToken`}
          />
          <FormikTextField
            label={t('fields.merchant_secret')}
            name={`${path}.merchantSecret`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.HiTrust && (
        <FormikTextField label={t('fields.api_key')} name={`${path}.apiKey`} />
      )}
      {providerType === P2PProviderType.PaymentLegacy && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_token')}
            name={`${path}.apiToken`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.JustMoney && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_token')}
            name={`${path}.apiToken`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Kazex && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikTextField
            label={t('fields.webhook_secret')}
            name={`${path}.callbackSecret`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Jakarta && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikTextField
            label={t('fields.secret_key')}
            name={`${path}.secretKey`}
          />
          <FormikTextField
            label={t('fields.default_bank')}
            name={`${path}.defaultPayinBank`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Almaty && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Alpina && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikTextField
            label={t('fields.webhook_secret')}
            name={`${path}.callbackSecret`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Argos && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_secret')}
            name={`${path}.apiSecret`}
          />
          <FormikTextField
            label={t('fields.merchant_id')}
            name={`${path}.merchantId`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.BayPay && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.BBBPay && (
        <Fragment>
          <FormikTextField
            label={t('fields.merchant_id')}
            name={`${path}.merchantId`}
          />
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Tokyo && (
        <Fragment>
          <FormikTextField
            label={t('fields.bearer_token')}
            name={`${path}.bearerToken`}
          />
          <FormikTextField
            label={t('fields.webhook_secret')}
            name={`${path}.webhookSecret`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Expay && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_public')}
            name={`${path}.apiPublic`}
          />
          <FormikTextField
            label={t('fields.api_private')}
            name={`${path}.apiPrivate`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.ExpayOne && (
        <Fragment>
          <FormikTextField
            label={t('fields.merchant_id')}
            name={`${path}.merchantId`}
          />
          <FormikTextField
            label={t('fields.access_token')}
            name={`${path}.accessToken`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.ProFiat && (
        <Fragment>
          <FormikTextField label={t('fields.uid')} name={`${path}.uid`} />
          <FormikTextField
            label={t('fields.shop_id')}
            name={`${path}.shopId`}
          />
          <FormikTextField
            label={t('fields.api_private')}
            name={`${path}.privateKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Kvell && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikTextField
            label={t('fields.api_secret')}
            name={`${path}.apiSecret`}
          />
          <FormikTextField
            label={t('fields.signature_key')}
            name={`${path}.privateKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.LazyTrade && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikTextField
            label={t('fields.secret_key')}
            name={`${path}.secretKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.LionTech && (
        <Fragment>
          <FormikTextField
            label={t('fields.access_token')}
            name={`${path}.accessToken`}
          />
          <FormikTextField
            label={t('fields.refresh_token')}
            name={`${path}.refreshToken`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.SkyPhoenix && (
        <Fragment>
          <FormikTextField
            label={t('fields.token_public')}
            name={`${path}.tokenPublic`}
          />
          <FormikTextField
            label={t('fields.token_private')}
            name={`${path}.tokenPrivate`}
          />
          <FormikTextField label={t('fields.email')} name={`${path}.email`} />
        </Fragment>
      )}
      {providerType === P2PProviderType.SBPay && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_token')}
            name={`${path}.apiToken`}
          />
          <FormikTextField
            label={t('fields.merchant_id')}
            name={`${path}.merchantId`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.FastPay && (
        <Fragment>
          <FormikTextField label={t('fields.token')} name={`${path}.token`} />
          <FormikTextField
            label={t('fields.secret_key')}
            name={`${path}.secretKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Havalepayhera && (
        <Fragment>
          <FormikTextField label={t('fields.api_key')} name={`${path}.key`} />
          <FormikTextField
            label={t('fields.secret_key')}
            name={`${path}.secret`}
          />
          <FormikSelect
            label={t('fields.flow_mode')}
            name={`${path}.flowMode`}
            options={[
              { value: 'requisite', label: 'Requisite' },
              { value: 'payment_link', label: 'Payment Link' },
            ]}
          />
          <FormikTextField
            label={t('fields.default_bank')}
            name={`${path}.defaultBank`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.FlowPay && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.RoyalFinance && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_token')}
            name={`${path}.apiToken`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Samarsky && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Aggregato && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Affinex && (
        <Fragment>
          <FormikTextField
            label={t('fields.username')}
            name={`${path}.username`}
          />
          <FormikTextField
            label={t('fields.password')}
            name={`${path}.password`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.SevenXPay && (
        <Fragment>
          <FormikTextField
            label={t('fields.merchant_id')}
            name={`${path}.merchantId`}
          />
          <FormikTextField
            label={t('fields.api_secret')}
            name={`${path}.apiSecret`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.PayUp && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_public')}
            name={`${path}.apiKey`}
          />
          <FormikTextField
            label={t('fields.api_private')}
            name={`${path}.privateKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Unite && (
        <Fragment>
          <FormikTextField label={t('fields.api_key')} name={`${path}.keyId`} />
          <FormikTextField
            label={t('fields.api_private')}
            name={`${path}.privateKey`}
          />
          <FormikTextField
            label={t('fields.api_public')}
            name={`${path}.serverPublicKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Vienna && (
        <Fragment>
          <FormikTextField
            label={t('fields.terminal_id')}
            name={`${path}.terminalId`}
          />
          <FormikTextField
            label={t('fields.secret_key')}
            name={`${path}.secretKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.PayVia && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_token')}
            name={`${path}.apiToken`}
          />
          <FormikTextField
            label={t('fields.merchant_id')}
            name={`${path}.merchantId`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Ampay && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_public')}
            name={`${path}.apiPublic`}
          />
          <FormikTextField
            label={t('fields.api_private')}
            name={`${path}.apiPrivate`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.TokenRanch && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikTextField
            label={t('fields.api_secret')}
            name={`${path}.apiSecret`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.TradeSide && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_public')}
            name={`${path}.publicKey`}
          />
          <FormikTextField
            label={t('fields.api_private')}
            name={`${path}.privateKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.ReactPay && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_token')}
            name={`${path}.apiToken`}
          />
          <FormikTextField
            label={t('fields.api_private')}
            name={`${path}.privateKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Rounin && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikTextField
            label={t('fields.api_secret')}
            name={`${path}.apiSecret`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Paylord && (
        <Fragment>
          <FormikTextField
            label={t('fields.merchant_id')}
            name={`${path}.merchantId`}
          />
          <FormikTextField label={t('fields.login')} name={`${path}.login`} />
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.PayGateCore && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_token')}
            name={`${path}.merchantToken`}
          />
          <FormikTextField
            label={t('fields.secret_key')}
            name={`${path}.secretKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Decard && (
        <Fragment>
          <FormikTextField
            label={t('fields.shop_key')}
            name={`${path}.shopKey`}
          />
          <FormikTextField
            label={t('fields.secret_key')}
            name={`${path}.secretKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Easy2Tap && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_token')}
            name={`${path}.apiToken`}
          />
          <FormikTextField
            label={t('fields.api_secret')}
            name={`${path}.apiSecret`}
          />
          <FormikTextField
            label={t('fields.merchant_id')}
            name={`${path}.merchantId`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Payshark && (
        <Fragment>
          <FormikTextField
            label={t('fields.merchant_id')}
            name={`${path}.merchantId`}
          />
          <FormikTextField
            label={t('fields.access_token')}
            name={`${path}.accessToken`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.TeleStore && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_public')}
            name={`${path}.publicKey`}
          />
          <FormikTextField
            label={t('fields.api_private')}
            name={`${path}.privateKey`}
          />
          <FormikTextField label={t('fields.app_id')} name={`${path}.appId`} />
          <FormikSelect
            label={t('fields.flow_mode')}
            name={`${path}.flowMode`}
            options={[
              { value: 'p2p', label: 'P2P' },
              { value: 'payment_link', label: 'Payment Link' },
            ]}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Tenet && (
        <Fragment>
          <FormikTextField
            label={t('fields.username')}
            name={`${path}.username`}
          />
          <FormikTextField
            label={t('fields.password')}
            name={`${path}.password`}
          />
          <FormikTextField
            label={t('fields.merchant_id')}
            name={`${path}.merchantId`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Piqpay && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikTextField
            label={t('fields.secret_key')}
            name={`${path}.secretKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.PrMoney && (
        <Fragment>
          <FormikTextField
            label={t('fields.client_id')}
            name={`${path}.clientId`}
          />
          <FormikTextField
            label={t('fields.api_secret')}
            name={`${path}.clientSecret`}
          />
          <FormikTextField
            label={t('fields.api_token')}
            name={`${path}.token`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.WorldPay && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_token')}
            name={`${path}.apiToken`}
          />
          <FormikTextField
            label={t('fields.api_secret')}
            name={`${path}.apiSecret`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.BillionPay && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikTextField
            label={t('fields.api_secret')}
            name={`${path}.apiSecret`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Transavex && (
        <Fragment>
          <FormikTextField
            label={t('fields.client_id')}
            name={`${path}.clientId`}
          />
          <FormikTextField
            label={t('fields.secret_key')}
            name={`${path}.secretKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.BucksBunny && (
        <Fragment>
          <FormikTextField
            label={t('fields.merchant_id')}
            name={`${path}.merchantId`}
          />
          <FormikTextField
            label={t('fields.api_secret')}
            name={`${path}.xSecret`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Vnd && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikTextField
            label={t('fields.secret_key')}
            name={`${path}.webhookSecret`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.LuxCore && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikTextField
            label={t('fields.api_secret')}
            name={`${path}.webhookSecret`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Meridian && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikTextField
            label={t('fields.api_secret')}
            name={`${path}.apiSecret`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.ScroogePay && (
        <Fragment>
          <FormikTextField
            label={t('fields.merchant_id')}
            name={`${path}.partnerId`}
          />
          <FormikTextField
            label={t('fields.secret_key')}
            name={`${path}.secretKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Sequoia && (
        <Fragment>
          <FormikTextField
            label={t('fields.secret_key')}
            name={`${path}.secretKey`}
          />
          <FormikTextField
            label={t('fields.webhook_secret')}
            name={`${path}.webhookSecret`}
          />
          <FormikTextField
            label={t('fields.merchant_id')}
            name={`${path}.merchantId`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.YummyPay && (
        <Fragment>
          <FormikTextField
            label={t('fields.merchant_id')}
            name={`${path}.merchant`}
          />
          <FormikTextField
            label={t('fields.api_secret')}
            name={`${path}.privateToken`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Zentra && (
        <Fragment>
          <FormikTextField
            label={t('fields.merchant_id')}
            name={`${path}.merchantId`}
          />
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikTextField
            label={t('fields.api_secret')}
            name={`${path}.apiSecret`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Fenix && (
        <Fragment>
          <FormikTextField label="Client ID" name={`${path}.clientId`} />
          <FormikTextField
            label="Client Secret"
            name={`${path}.clientSecret`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Finex && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikTextField
            label={t('fields.webhook_secret')}
            name={`${path}.webhookSecret`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.RiskPay && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikTextField
            label={t('fields.webhook_secret')}
            name={`${path}.webhookSecret`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.PayfinityV2 && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_public')}
            name={`${path}.publicKey`}
          />
          <FormikTextField
            label={t('fields.api_private')}
            name={`${path}.privateKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Psp24 && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikTextField
            label={t('fields.webhook_secret')}
            name={`${path}.webhookSecret`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Edro && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Upay && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_token')}
            name={`${path}.apiToken`}
          />
        </Fragment>
      )}
    </Fragment>
  );
};
